<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import axios from 'axios'
import type { Payment, StageCode, StageSetup, ThankYouPage } from '@/types'

import UiHeader from '@/components/UiHeader/UiHeader.vue'
import UiStatusCard from '@/components/UiStatusCard/UiStatusCard.vue'
import UiTable from '@/components/UiTable/UiTable.vue'
import UiFooter from '@/components/UiFooter/UiFooter.vue'
import AppHeader from '@/layouts/AppHeader/AppHeader.vue'
import PartnerSuccessView from '@/view/PartnerSuccessView.vue'

const route = useRoute()
const isPartner = route.query.partner === 'pokataev'
const isPreview = !!route.query.preview_thp
const stageCode = ref<StageCode>('Success')
const payment = ref<Payment>()
const orderNumber = ref<string>()
const previewThp = ref<ThankYouPage>()

const isLoaded = ref<boolean>(false)

const stageSetups: Record<StageCode, StageSetup> = {
  Success: {
    title: 'Успешно',
    subtitle: 'Спасибо за покупку',
    description:
      'Платёж успешно завершен. <br/>Вернитесь на сайт магазина или свяжитесь с продавцом',
    icon: 'https://storage.yandexcloud.net/terpay/success-icon.png',
    color: '#C9FFB6'
  },
  WaitSign: {
    title: 'Ожидает подписания',
    subtitle: 'Заявка одобрена',
    description: 'Подпишите договор с банком',
    icon: null,
    color: '#b6e7ff'
  },
  Pending: {
    title: 'Ожидаем ответ от банка',
    subtitle: 'Мы скоро сменим статус платежа',
    description: 'Ожидаем ответ от банка. <br/>Пожалуйста, обновите страницу через несколько минут',
    icon: null,
    color: '#b6e7ff'
  },
  Error: {
    title: 'Ошибка',
    subtitle: 'Cистема отклонила платеж',
    description: 'Вернитесь на сайт магазина или свяжитесь с продавцом',
    icon: 'https://storage.yandexcloud.net/terpay/error-icon.png',
    color: '#FFDBDB'
  },
  Canceled: {
    title: 'Отмена',
    subtitle: 'Заявка отменена',
    description: 'Вернитесь на сайт магазина или свяжитесь с продавцом',
    icon: null,
    color: '#FFDBDB'
  }
}

const stage = computed<StageSetup>(() => stageSetups[stageCode.value])

const thp = computed<ThankYouPage | undefined>(() => isPreview ? previewThp.value : payment.value?.thp)

async function getPayment(uuid: string) {
  try {
    const response = await axios.get<Payment>(`/payments/${uuid}`)
    payment.value = response.data
    orderNumber.value = uuid
    stageCode.value = response.data.stage
  } catch (error) {
    console.warn(`Payment ${uuid} not found, showing default success page`, error)
  }
}

async function loadPreviewThp(id: string) {
  try {
    const response = await axios.get<ThankYouPage>(`/thp-preview/${id}`)
    previewThp.value = response.data
  } catch (error) {
    console.warn(`ThankYouPage ${id} not found`, error)
  }
}

onMounted(async () => {
  if (isPreview) {
    await loadPreviewThp(`${route.query.preview_thp}`)
    isLoaded.value = true
    return
  }

  const orderNumber = `${route.query.order_number || ''}`
  if (orderNumber) {
    await getPayment(orderNumber)
  }

  if (payment.value?.thp?.success_url) {
    window.location.href = payment.value.thp.success_url
  }

  isLoaded.value = true
})
</script>

<template>
  <partner-success-view v-if="isPartner" />
  <div v-else-if="isLoaded" class="payment-cabinet">
    <div class="">
      <app-header />

      <ui-status-card :stage="stage" :thp="thp" />

      <ui-table
        v-if="payment || isPreview"
        :order-number="orderNumber"
        :date="payment?.status_change_at"
        :provider-title="payment ? `${payment.provider.title} ${payment.action.title ? `(${payment.action.title})` : ''}` : ''"
        :provider-type="payment?.provider.type"
        :total-price="payment?.sum"
        :ofd-receipt-url="payment?.ofd_receipt_url"
        :thp="thp"
        :stage-code="stageCode"
        :stage="stage"
      />

      <ui-footer />
    </div>
  </div>
</template>

<style lang="scss">

</style>
